import { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { ContributorEntityMetric } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface EntityMetricsTableProps {
  entities: ContributorEntityMetric[];
  loading: boolean;
}

type SortField = 'name' | 'fitnessScore' | 'invocationCount' | 'successRate' | 'lastInvokedAt';
type SortDirection = 'asc' | 'desc';

export function EntityMetricsTable({ entities, loading }: EntityMetricsTableProps) {
  const [sortField, setSortField] = useState<SortField>('fitnessScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedEntities = useMemo(() => {
    let filtered = entities;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = entities.filter((e) => e.status === statusFilter);
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'fitnessScore':
          aVal = a.fitnessScore;
          bVal = b.fitnessScore;
          break;
        case 'invocationCount':
          aVal = a.invocationCount;
          bVal = b.invocationCount;
          break;
        case 'successRate':
          aVal = a.successRate;
          bVal = b.successRate;
          break;
        case 'lastInvokedAt':
          aVal = a.lastInvokedAt ?? '';
          bVal = b.lastInvokedAt ?? '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [entities, sortField, sortDirection, statusFilter]);

  const SortHeader = ({
    field,
    children,
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <TableHead
      onClick={() => handleSort(field)}
      className="cursor-pointer hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === 'asc'
            ? <ArrowUp className="h-3 w-3 text-muted-foreground" />
            : <ArrowDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </TableHead>
  );

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'published':
        return 'default';
      case 'deprecated':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'testing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'deprecated':
        return 'bg-destructive/20 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getFitnessColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 dark:text-green-400';
    if (score >= 0.6) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 0.4) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* Filter */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Label htmlFor="statusFilter" className="text-sm">
            Filter by status:
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="statusFilter" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="testing">Testing</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden">
        {sortedEntities.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No entities found
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedEntities.map((entity) => (
              <div key={entity.entityId} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 min-w-0 mr-2">
                    <h3 className="font-medium truncate">{entity.name}</h3>
                    <span className="text-xs text-muted-foreground font-mono">
                      {entity.entityId.slice(0, 12)}...
                    </span>
                  </div>
                  <Badge
                    variant={getStatusVariant(entity.status)}
                    className={cn("capitalize flex-shrink-0", getStatusClasses(entity.status))}
                  >
                    {entity.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Fitness: </span>
                    <span className={cn("font-medium", getFitnessColor(entity.fitnessScore))}>
                      {(entity.fitnessScore * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Success: </span>
                    <span>{(entity.successRate * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Invocations: </span>
                    <span>{entity.invocationCount.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Used: </span>
                    <span>{formatDate(entity.lastInvokedAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHeader field="name">Name</SortHeader>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <SortHeader field="fitnessScore">Fitness</SortHeader>
              <SortHeader field="invocationCount">Invocations</SortHeader>
              <SortHeader field="successRate">Success Rate</SortHeader>
              <TableHead>Avg Latency</TableHead>
              <SortHeader field="lastInvokedAt">Last Used</SortHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedEntities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No entities found
                </TableCell>
              </TableRow>
            ) : (
              sortedEntities.map((entity) => (
                <TableRow key={entity.entityId}>
                  <TableCell>
                    <div className="font-medium">{entity.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {entity.entityId.slice(0, 16)}...
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground capitalize">
                    {entity.type}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getStatusVariant(entity.status)}
                      className={cn("capitalize", getStatusClasses(entity.status))}
                    >
                      {entity.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={cn("font-medium", getFitnessColor(entity.fitnessScore))}>
                      {(entity.fitnessScore * 100).toFixed(0)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entity.invocationCount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(entity.successRate * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entity.avgLatencyMs !== null ? `${entity.avgLatencyMs}ms` : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(entity.lastInvokedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
